import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search, MapPin, Star, Target, CheckCircle2, AlertTriangle, FileWarning, Users2, X
} from "lucide-react";
import { PageHeader } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";
import { fmtCr, displayTheme } from "../utils/format";
import { fabricatePartnerExtras, fmtBeneficiaries } from "../utils/seededFabricators";
import { getThemeDotColor } from "../utils/dynamicColors";

/** Utilisation % pill colors. */
function utilColor(pct) {
  if (pct >= 80) return "bg-emerald-100 text-emerald-800";
  if (pct >= 60) return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-700";
}

function reportIcon(tone) {
  if (tone === "good") return <CheckCircle2 size={12} className="text-emerald-600" />;
  if (tone === "warn") return <AlertTriangle size={12} className="text-amber-600" />;
  return <FileWarning size={12} className="text-rose-600" />;
}

function reportClass(tone) {
  if (tone === "good") return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (tone === "warn") return "bg-amber-50 text-amber-700 border-amber-100";
  return "bg-rose-50 text-rose-700 border-rose-100";
}

function PartnerCard({ row }) {
  const sanctioned = row.allocation_cr || 0;
  const utilised = row.utilised_cr || 0;
  const utilPct = sanctioned > 0 ? Math.min(100, Math.round((utilised / sanctioned) * 100)) : 0;
  const extras = fabricatePartnerExtras(row.grantee, row.grants);
  const themeColor = getThemeDotColor(row.theme);

  return (
    <article
      data-testid={`partner-card-${row.grantee}`}
      className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col"
    >
      {/* Header: theme pill + name */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full"
          style={{ backgroundColor: `${themeColor}1f`, color: themeColor }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: themeColor }} />
          {displayTheme(row.theme)}
        </span>
      </div>
      <h3 className="text-[15px] font-bold text-slate-800 leading-snug" data-testid="partner-name">
        {row.grantee}
      </h3>

      {/* States */}
      <div className="mt-1.5 flex items-start gap-1 text-[11px] text-slate-500">
        <MapPin size={11} className="mt-0.5 flex-shrink-0" />
        <span className="leading-snug">
          <span className="font-medium text-slate-600">States: </span>
          {extras.states.join(", ")}
        </span>
      </div>

      {/* Metrics row */}
      <div className="mt-3 grid grid-cols-4 gap-2 pt-3 border-t border-slate-100">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-slate-500 font-medium">Beneficiaries</div>
          <div className="text-[15px] font-bold text-slate-800 mt-0.5">{fmtBeneficiaries(extras.beneficiaries)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-slate-500 font-medium">Sanctioned</div>
          <div className="text-[15px] font-bold text-slate-800 mt-0.5 whitespace-nowrap">{fmtCr(sanctioned)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-slate-500 font-medium">Utilised</div>
          <div className="text-[13px] font-bold text-slate-800 mt-0.5 flex items-baseline gap-1 whitespace-nowrap">
            {fmtCr(utilised)}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${utilColor(utilPct)}`}>
              {utilPct}%
            </span>
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-slate-500 font-medium"># of Grants</div>
          <div className="text-[15px] font-bold text-slate-800 mt-0.5">{row.grants}</div>
        </div>
      </div>

      {/* Performance & status badges */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full">
          <Star size={11} className="fill-amber-400 text-amber-400" />
          {extras.rating.toFixed(2)}/5
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
          <Target size={11} />
          {extras.kpiPct}% KPIs
        </span>
        <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border ${reportClass(extras.reportStatus.tone)}`}>
          {reportIcon(extras.reportStatus.tone)}
          {extras.reportStatus.label}
        </span>
      </div>

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500">
        Partnering since {extras.partneringSince}
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
      <div className="h-4 w-20 bg-slate-100 rounded-full mb-3" />
      <div className="h-4 w-3/4 bg-slate-200 rounded mb-2" />
      <div className="h-3 w-1/2 bg-slate-100 rounded mb-4" />
      <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-100">
        {[...Array(4)].map((_, i) => (
          <div key={i}>
            <div className="h-2 w-12 bg-slate-100 rounded mb-1" />
            <div className="h-4 w-10 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Partners() {
  const { filters, filterOptions, viewMode, API } = useFilters();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [themeFilter, setThemeFilter] = useState(null);

  const params = useMemo(
    () => buildDashboardParams(filters, { view_mode: viewMode }),
    [filters, viewMode]
  );

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/grantee/list`, { params: { ...params, per_page: 500 } })
      .then((r) => setRows(r.data.data || []))
      .catch((e) => {
        console.error(e);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [API, params]);

  // Theme list driven from data + dashboard filter options.
  // Dedupe by display name to avoid "Institutions" vs "Institutional Strengthening" duplicates.
  const themeOptions = useMemo(() => {
    const fromData = new Set(rows.map((r) => r.theme).filter(Boolean));
    (filterOptions.theme_options || []).forEach((t) => fromData.add(t));
    const byDisplay = new Map();
    Array.from(fromData).forEach((t) => {
      const d = displayTheme(t);
      if (!byDisplay.has(d)) byDisplay.set(d, t);
    });
    return Array.from(byDisplay.values()).sort((a, b) => displayTheme(a).localeCompare(displayTheme(b)));
  }, [rows, filterOptions.theme_options]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const activeDisplay = themeFilter ? displayTheme(themeFilter) : null;
    return rows.filter((r) => {
      if (activeDisplay && displayTheme(r.theme) !== activeDisplay) return false;
      if (q && !(r.grantee || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, themeFilter, search]);

  const totalSanctioned = useMemo(
    () => filtered.reduce((s, r) => s + (r.allocation_cr || 0), 0),
    [filtered]
  );

  return (
    <div className="space-y-4" data-testid="partners-page">
      <PageHeader subtitle="" />

      {/* Hero header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users2 size={20} className="text-[#006837]" />
            Implementation Partners
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {filtered.length} partner{filtered.length === 1 ? "" : "s"} delivering programmes on the ground
            {themeFilter ? ` · ${displayTheme(themeFilter)}` : " across all themes"}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Total Sanctioned</div>
          <div className="text-lg font-bold text-[#006837] mt-0.5" data-testid="partners-total-sanctioned">
            {fmtCr(totalSanctioned)}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="partners-search-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search partners..."
          className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#006837]/20 focus:border-[#006837]"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Theme pill filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="partners-theme-filters">
        <button
          onClick={() => setThemeFilter(null)}
          data-testid="theme-filter-all"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            themeFilter === null
              ? "bg-[#006837] text-white border-[#006837]"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          All
        </button>
        {themeOptions.map((t) => {
          const active = themeFilter === t;
          const color = getThemeDotColor(t);
          return (
            <button
              key={t}
              onClick={() => setThemeFilter(active ? null : t)}
              data-testid={`theme-filter-${t}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                active
                  ? "text-white border-transparent"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              style={active ? { backgroundColor: color, borderColor: color } : undefined}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: active ? "rgba(255,255,255,0.95)" : color }}
              />
              {displayTheme(t)}
            </button>
          );
        })}
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[...Array(9)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-12 flex flex-col items-center justify-center text-slate-500">
          <Users2 size={36} className="opacity-40 mb-2" />
          <p className="text-sm">No partners match your search</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="partners-grid">
          {filtered.map((r) => (
            <PartnerCard key={`${r.grantee}-${r.theme}`} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}
